import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./app/App";
import { isTafoAppPath, useInTafoApp } from "./app/switch-screen";
import { markDemoTill } from "./app/demo";
import { deviceToken } from "./app/device-token";
import { takeSupportFromAddress } from "./app/support";
import { SupportApp } from "./app/SupportBar";
import { isBeheer } from "./beheer/routes";
import { folderToken } from "./features/folder/folder-token";
import { NewsletterConfirmPage, newsletterConfirmToken } from "./features/newsletter/NewsletterConfirmPage";
import { NewsletterPage, newsletterToken } from "./features/newsletter/NewsletterPage";
import { PrintPage, printJobId } from "./features/printing/PrintPage";
import { tableOrderToken } from "./features/table-order/table-order-token";
import { TrackPage, trackToken } from "./features/track/TrackPage";
import { TafoApp } from "./tafo-app/TafoApp";
import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/till.css";

/** Tafo-beheer (blok K, ADR 0012): only loaded for the operators, at /beheer or on beheer.<domein>. */
const BeheerApp = lazy(() => import("./beheer/BeheerApp").then((module) => ({ default: module.BeheerApp })));
/** The download page of Tafo for Windows (tafo.be/install): a page of its own, no till behind it. */
const InstallPage = lazy(() =>
  import("./features/install/InstallPage").then((module) => ({ default: module.InstallPage })),
);
/** The privacy statement of the Tafo app (app.tafo.be/privacy): the link in the App Store and Google Play. */
const PrivacyPage = lazy(() =>
  import("./features/privacy/PrivacyPage").then((module) => ({ default: module.PrivacyPage })),
);
const InvitationApp = lazy(() => import("./beheer/BeheerApp").then((module) => ({ default: module.InvitationApp })));

/** The folder of the shop to print (ADR 0050), opened from Beheer › Menu › Folder: a page of its own, no till. */
const FolderPage = lazy(() =>
  import("./features/folder/FolderPage").then((module) => ({ default: module.FolderPage })),
);

/** The guest's page behind the QR code on a table (QR-bestellen aan tafel): his own phone, no till. */
const TableOrderPage = lazy(() =>
  import("./features/table-order/TableOrderPage").then((module) => ({ default: module.TableOrderPage })),
);

/** The customer's page of an order ("Volg je bestelling live" in the mail) needs no till. */
const track = trackToken(window.location.pathname);
/** Nor the guest's page at a table (/tafel/<token>). */
const tableOrder = tableOrderToken(window.location.pathname);
/** Nor does the page behind "uitschrijven" in the mail about the Dagpromo. */
const newsletter = newsletterToken(window.location.pathname);
/** Nor the page behind the link that confirms the newsletter (Furkan, 30/10/2026). */
const confirmNewsletter = newsletterConfirmToken(window.location.pathname);
/** "Folder maken": the folder of a shop behind a link of 2 hours. */
const folder = folderToken(window.location.pathname);
/** The print page of the Tafo app for Windows (blok I): one job, drawn on the paper's width. */
const printJob = printJobId(window.location.pathname);
/** The Tafo app for waiters and couriers (blok J, ADR 0011): a phone, tablet or Sunmi opens /app. */
const tafoApp = isTafoAppPath(window.location.pathname);
/** tafo.be/install, also on the address of Tafo-beheer: the page to download Tafo for Windows. */
const install = /^\/install\/?$/.test(window.location.pathname);
/** /privacy: a page to read, also on the address of Tafo-beheer; no till behind it. */
const privacy = /^\/privacy\/?$/.test(window.location.pathname);
const beheer = !install && !privacy && isBeheer(window.location);
/** The link in the welcome mail of a new owner: choosing a password. */
const invitation = /^\/uitnodiging\/([^/]+)\/?$/.exec(window.location.pathname)?.[1] ?? null;
/** 🛟 Inloggen als zaak: Tafo works on the shop's till in a window of its own, with a yellow bar. */
const support = !install && !privacy && !beheer && invitation === null && takeSupportFromAddress(window.location);

// the Tafo app for Windows prints with this till's token (ADR 0010)
if (
  track === null &&
  tableOrder === null &&
  newsletter === null &&
  confirmNewsletter === null &&
  folder === null &&
  printJob === null &&
  !tafoApp &&
  !beheer &&
  !support &&
  !install &&
  !privacy
)
  deviceToken.announce();
// the till itself, and no other page: in the demo its tab is the one a new start of the demo brings back
if (
  track === null &&
  tableOrder === null &&
  newsletter === null &&
  confirmNewsletter === null &&
  folder === null &&
  printJob === null &&
  !tafoApp &&
  !beheer &&
  invitation === null &&
  !support &&
  !install &&
  !privacy
)
  markDemoTill();

/** The Tafo app or the till: the owner's phone goes from one to the other in the same page (switch-screen.ts). */
function AppOrTill() {
  return useInTafoApp() ? <TafoApp /> : <App />;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    {install ? (
      <Suspense fallback={null}>
        <InstallPage />
      </Suspense>
    ) : privacy ? (
      <Suspense fallback={null}>
        <PrivacyPage />
      </Suspense>
    ) : beheer ? (
      <Suspense fallback={null}>
        <BeheerApp />
      </Suspense>
    ) : invitation !== null ? (
      <Suspense fallback={null}>
        <InvitationApp token={decodeURIComponent(invitation)} />
      </Suspense>
    ) : track !== null ? (
      <TrackPage token={track} />
    ) : tableOrder !== null ? (
      <Suspense fallback={null}>
        <TableOrderPage token={tableOrder} />
      </Suspense>
    ) : newsletter !== null ? (
      <NewsletterPage token={newsletter} />
    ) : confirmNewsletter !== null ? (
      <NewsletterConfirmPage token={confirmNewsletter} />
    ) : folder !== null ? (
      <Suspense fallback={null}>
        <FolderPage token={folder} />
      </Suspense>
    ) : printJob !== null ? (
      <PrintPage jobId={printJob} />
    ) : support ? (
      <SupportApp />
    ) : (
      <AppOrTill />
    )}
  </StrictMode>,
);
